import React, { useState } from 'react';
import { sirenActions, entityStore, useSirenEntity, useMeasurement, conversions } from '~/utility/BinSentry-ui-utility';
import { makeStyles,
  DialogContent,
  DialogContentText,
  FormControl,
  FormHelperText,
  Input,
  MenuItem,
  Select,
} from '~/lib/bn-material-ui';
import { BnDialog } from '~/common';

const useStyles = makeStyles(() => {
  return {
    marginRight: {
      marginRight: '1em',
    },
    dialogContent: {
      marginBottom: '1em',
    },
  };
});

const units = [
  { name: 'cubic_feet', display: 'ft³' },
  { name: 'cubic_meter', display: 'm³' },
  { name: 'metric-ton', display: 'MT' },
  { name: 'short-ton', display: 'T' },
  { name: 'kilogram', display: 'kg' },
  { name: 'pound', display: 'lbs' },
];

const displayToName = display => units.find(u => u.display === display)?.name || display;
const nameToDisplay = name => units.find(u => u.name === name)?.display || name;

const setManufacturersCapacity = async (capacity, unit, entity) => {
  const converted = conversions.orderAmountPrefToKilograms(capacity, unit);
  const action = entity.getAction('update-details');
  const fields = sirenActions.getActionFields(action);
  fields.set('listedCapacity', converted);
  await sirenActions.submitAction(action, fields);
  await entityStore.invalidateHref(entity.getLinkByRel('self').href);
  const levelsHref = entity.getSubEntityByRel('https://api.binsentry.com/rel/v2/bin-levels')?.href;
  if (levelsHref) {
    await entityStore.invalidateHref(levelsHref);
  }
};

export default function ManufacturersCapacity({ handleClose, show, href, oldCapacity = 0 }) {
  const styles = useStyles();
  const pref = useMeasurement({ name: 'orderAmount' });
  const { entity } = useSirenEntity({ href });
  const [capacity, setCapacity] = useState(conversions.kilogramsToOrderAmountPref(oldCapacity, pref.unit));
  const [unit, setUnit] = useState(displayToName(pref.unit));

  const updateUnit = newUnit => {
    if (!newUnit || newUnit === unit) {
      return;
    }
    const kgs = conversions.orderAmountPrefToKilograms(capacity, unit);
    setCapacity(conversions.kilogramsToOrderAmountPref(kgs, nameToDisplay(newUnit)));
    setUnit(newUnit);
  };

  const onSubmit = async () => {
    await setManufacturersCapacity(capacity, unit, entity);
    handleClose();
  };

  return (
    <BnDialog
      open={show}
      onClose={() => handleClose()}
      title={'Manufacturer\'s Listed Capacity'}
      onSubmit={onSubmit}
      submitLabel='Save'
    >
      <DialogContent>
        <DialogContentText className={styles.dialogContent}>
          The capacity of the bin is required for calculating bin levels and forecasting.
          Providing the capacity as a volume (cubic feet) provides the most accuracy.
        </DialogContentText>

        <FormControl className={styles.marginRight}>
          <Input
            type='number'
            value={parseFloat(capacity?.toFixed(2))}
            onChange={e => setCapacity(parseFloat(e.target.value))}
            aria-describedby='capacity-measure'
            inputProps={{ min: '0' }}
          />
          <FormHelperText id='capacity-measure'>Bin Capacity</FormHelperText>
        </FormControl>

        <FormControl>
          <Select
            labelId='capacity-unit-label'
            value={unit}
            onChange={e => updateUnit(e.target.value)}
          >
            <MenuItem value='cubic_feet'>ft&sup3; - Cubic feet</MenuItem>
            <MenuItem value='cubic_meter'>m&sup3; - Cubic meters</MenuItem>
            <MenuItem value='metric-ton'>MT - Metric tonnes</MenuItem>
            <MenuItem value='short-ton'>T - Tons</MenuItem>
            <MenuItem value='kilogram'>kg - Kilograms</MenuItem>
            <MenuItem value='pound'>lb - Pounds</MenuItem>
          </Select>
          <FormHelperText id='capacity-unit-label'>Units</FormHelperText>
        </FormControl>
      </DialogContent>
    </BnDialog >
  );
}
